/**
 * @fileoverview Pure drag-and-drop state helpers for the template canvas.
 * @module template-builder
 *
 * Responsibilities:
 * - Calculate form update payloads for canvas field moves.
 * - Calculate form update payloads for palette field insertion into sections.
 * - Keep cross-section and cross-form drag logic deterministic and unit-testable.
 *
 * Dependencies:
 * - Template domain contracts from templateTypes
 *
 * Architecture:
 * - Local-first safe pure helpers; they do not call APIs, Dexie, or sync services.
 * - Callers persist returned updates through host-provided local-first callbacks.
 *
 * Notes:
 * - These helpers intentionally return immutable update payloads rather than mutating forms.
 */
import type { FormField, LayoutSection, TemplateBuilderForm } from '../../types/templateTypes';

/**
 * @contract FieldDropTarget
 *
 * Purpose:
 * - Represents the resolved canvas location where a dragged field should be inserted.
 *
 * Fields:
 * - formId: Destination form identifier.
 * - sectionId: Destination layout section identifier.
 * - insertIndex: Destination index within the section fieldIds array.
 *
 * Rules:
 * - insertIndex is clamped by the move calculation before updates are produced.
 * - Destination forms with module or header formKind are rejected by the move calculation.
 *
 * Sync:
 * - Local-first callback consumers write the calculated updates into Dexie-backed state.
 */
export interface FieldDropTarget {
  formId: string;
  sectionId: string;
  insertIndex: number;
}

/**
 * @contract FieldLocation
 *
 * Purpose:
 * - Describes the current location of a field in the canvas layout graph.
 *
 * Fields:
 * - formId: Current owning form identifier.
 * - section: Current containing section.
 * - fieldIndex: Current index inside section.fieldIds.
 * - field: Full field contract from the owning form.
 *
 * Rules:
 * - A field location exists only when the field is both in form.fields and section.fieldIds.
 *
 * Sync:
 * - Read-only derived state; no server or Dexie access occurs here.
 */
export interface FieldLocation {
  formId: string;
  section: LayoutSection;
  fieldIndex: number;
  field: FormField;
}

/**
 * @contract FieldMoveUpdate
 *
 * Purpose:
 * - Represents a single form patch needed to complete a canvas field move.
 *
 * Fields:
 * - formId: Form receiving the update payload.
 * - updates: Partial TemplateBuilderForm patch for host persistence callbacks.
 *
 * Rules:
 * - Same-form moves return one layout-only patch.
 * - Cross-form moves return source and target patches.
 *
 * Sync:
 * - Host components pass updates into local-first onUpdateForm callbacks.
 */
export interface FieldMoveUpdate {
  formId: string;
  updates: Partial<TemplateBuilderForm>;
}

/**
 * @contract FieldInsertUpdate
 *
 * Purpose:
 * - Represents the form patch needed to insert a newly created palette field.
 *
 * Fields:
 * - formId: Form receiving the inserted field.
 * - updates: Partial TemplateBuilderForm patch containing fields and layout updates.
 *
 * Rules:
 * - Insert index is clamped to the destination section bounds.
 * - Header and module forms reject field insertion.
 *
 * Sync:
 * - Host components pass updates into local-first onUpdateForm callbacks.
 */
export interface FieldInsertUpdate {
  formId: string;
  updates: Partial<TemplateBuilderForm>;
}

/**
 * @process FindCanvasFieldLocation
 *
 * Steps:
 * 1. Iterate forms in the current canvas map.
 * 2. Find the field contract in each form.fields collection.
 * 3. Confirm the field is referenced by a layout section.
 *
 * Inputs:
 * - forms: Current canvas form map.
 * - fieldId: Field identifier to locate.
 *
 * Outputs:
 * - FieldLocation when found, otherwise null.
 *
 * Failure Handling:
 * - Missing fields or orphaned section references return null.
 *
 * Idempotency: REQUIRED — repeated calls with the same map return the same location.
 */
export function findCanvasFieldLocation(forms: Map<string, TemplateBuilderForm>, fieldId: string): FieldLocation | null {
  for (const [formId, form] of forms) {
    const field = (form.fields ?? []).find((candidate) => candidate.id === fieldId);
    if (!field) continue;

    for (const section of form.layout?.sections ?? []) {
      const fieldIndex = section.fieldIds.indexOf(fieldId);
      if (fieldIndex !== -1) return { formId, section, fieldIndex, field };
    }
  }
  return null;
}

/**
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - Produces immutable update payloads only; callers write through local-first form services.
 *
 * Sync Strategy:
 * - Background sync is triggered by the host callback layer after local state is updated.
 *
 * Conflict Resolution:
 * - Existing form update conflict handling remains owned by the host local-first service.
 */
/**
 * @errorHandling
 *
 * Strategy:
 * - Invalid source, missing target, and locked destination forms return an empty update list.
 *
 * User Impact:
 * - Invalid drops are ignored without corrupting canvas layout state.
 *
 * Logging:
 * - REQUIRED at host workflow boundaries when callbacks fail; this pure helper does not log.
 */
export function buildMovedFieldFormUpdates(
  forms: Map<string, TemplateBuilderForm>,
  fieldId: string,
  target: FieldDropTarget,
): FieldMoveUpdate[] {
  const sourceLocation = findCanvasFieldLocation(forms, fieldId);
  if (!sourceLocation) return [];

  const sourceForm = forms.get(sourceLocation.formId);
  const targetForm = forms.get(target.formId);
  if (!sourceForm || !targetForm || targetForm.formKind === 'module' || targetForm.formKind === 'header') return [];

  if (sourceLocation.formId === target.formId) {
    const updatedSections = (targetForm.layout?.sections ?? []).map((section) => {
      const withoutField = section.fieldIds.filter((id) => id !== fieldId);
      if (section.id !== target.sectionId) return { ...section, fieldIds: withoutField };

      const sameSection = sourceLocation.section.id === target.sectionId;
      const adjustedIndex = sameSection && target.insertIndex > sourceLocation.fieldIndex
        ? target.insertIndex - 1
        : target.insertIndex;
      const clampedIndex = Math.max(0, Math.min(withoutField.length, adjustedIndex));
      const nextFieldIds = [...withoutField];
      nextFieldIds.splice(clampedIndex, 0, fieldId);
      return { ...section, fieldIds: nextFieldIds };
    });

    return [{
      formId: target.formId,
      updates: {
        layout: { ...(targetForm.layout ?? {}), sections: updatedSections },
      },
    }];
  }

  const sourceSections = (sourceForm.layout?.sections ?? []).map((section) => ({
    ...section,
    fieldIds: section.fieldIds.filter((id) => id !== fieldId),
  }));
  const targetSections = (targetForm.layout?.sections ?? []).map((section) => {
    if (section.id !== target.sectionId) return section;
    const nextFieldIds = [...section.fieldIds];
    const clampedIndex = Math.max(0, Math.min(nextFieldIds.length, target.insertIndex));
    nextFieldIds.splice(clampedIndex, 0, fieldId);
    return { ...section, fieldIds: nextFieldIds };
  });

  return [
    {
      formId: sourceLocation.formId,
      updates: {
        fields: (sourceForm.fields ?? []).filter((field) => field.id !== fieldId),
        layout: { ...(sourceForm.layout ?? {}), sections: sourceSections },
      },
    },
    {
      formId: target.formId,
      updates: {
        fields: [...(targetForm.fields ?? []), sourceLocation.field],
        layout: { ...(targetForm.layout ?? {}), sections: targetSections },
      },
    },
  ];
}

/**
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - Produces an immutable update payload only; callers write through local-first form services.
 *
 * Sync Strategy:
 * - Background sync is triggered by the host callback layer after local state is updated.
 *
 * Conflict Resolution:
 * - Existing form update conflict handling remains owned by the host local-first service.
 */
/**
 * @errorHandling
 *
 * Strategy:
 * - Missing forms, locked forms, missing sections, and invalid targets return null.
 *
 * User Impact:
 * - Invalid palette drops are ignored without corrupting canvas layout state.
 *
 * Logging:
 * - REQUIRED at host workflow boundaries when callbacks fail; this pure helper does not log.
 */
export function buildInsertedFieldFormUpdate(
  forms: Map<string, TemplateBuilderForm>,
  target: FieldDropTarget,
  newField: FormField,
): FieldInsertUpdate | null {
  const form = forms.get(target.formId);
  if (!form || form.formKind === 'module' || form.formKind === 'header') return null;

  let foundTargetSection = false;
  const updatedSections = (form.layout?.sections ?? []).map((section) => {
    if (section.id !== target.sectionId) return section;

    foundTargetSection = true;
    const nextFieldIds = [...section.fieldIds];
    const clampedIndex = Math.max(0, Math.min(nextFieldIds.length, target.insertIndex));
    nextFieldIds.splice(clampedIndex, 0, newField.id);
    return { ...section, fieldIds: nextFieldIds };
  });

  if (!foundTargetSection) return null;

  return {
    formId: target.formId,
    updates: {
      fields: [...(form.fields ?? []), newField],
      layout: { ...(form.layout ?? {}), sections: updatedSections },
    },
  };
}
